import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import PublicLeadForm from "@/components/PublicLeadForm";
import { formatPrice } from "@/lib/constants";

export default function PublicUnitPresentation() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [imageIndex, setImageIndex] = useState(null);
  const [planModal, setPlanModal] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/units/${id}`).then(({ data }) => setData(data));
  }, [id]);

  if (!data) return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500">Yükleniyor…</div>;

  const { unit, project, company } = data;
  const primary = company?.primary_color || "#0F172A";
  const accent = company?.secondary_color || "#F59E0B";
  const unitImages = unit.image_urls || [];
  const activeImageUrl = imageIndex != null ? unitImages[imageIndex] : null;
  const unitVideoEmbedUrl = getVideoEmbedUrl(unit.video_url);
  const showPrevImage = () => setImageIndex((i) => (i == null ? 0 : (i - 1 + unitImages.length) % unitImages.length));
  const showNextImage = () => setImageIndex((i) => (i == null ? 0 : (i + 1) % unitImages.length));

  return (
    <div className="min-h-screen" data-testid="public-unit-page" style={{ background: "#FAFAF7" }}>
      <header className="bg-white border-b" style={{ borderColor: "#E5E7EB" }}>
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            {company?.logo_url ? (
              <img src={company.logo_url} alt="" className="h-10" />
            ) : (
              <div className="h-10 w-10 rounded-lg flex items-center justify-center text-white font-bold" style={{ background: primary }}>
                {company?.name?.[0] || "F"}
              </div>
            )}
            <div>
              <div className="text-base font-semibold" style={{ color: primary }}>{company?.name}</div>
              <div className="text-xs text-slate-500">{project?.name}</div>
            </div>
          </div>
        </div>
      </header>

      {project?.cover_image_url && (
        <div className="aspect-[21/8] w-full bg-slate-200 overflow-hidden">
          <img src={project.cover_image_url} alt="" className="w-full h-full object-cover" />
        </div>
      )}

      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">{project?.name} • {unit.block_name} • {unit.floor_name}</div>
            <h1 className="text-4xl md:text-5xl font-bold font-display" style={{ color: primary }}>{unit.unit_no}</h1>
            <div className="mt-2 text-slate-600">
              {[unit.unit_type, unit.room_type, unit.layout_type].filter(Boolean).join(" • ")}
            </div>
          </div>
          {project?.public_show_prices && unit.price != null && <div className="text-right">
            <div className="text-xs uppercase tracking-wider text-slate-500">Fiyat</div>
            <div className="text-3xl md:text-4xl font-bold font-display" style={{ color: accent }}>{formatPrice(unit.price, unit.currency)}</div>
          </div>}
        </div>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
          {unit.layout_type && <Box l="Plan Tipi" v={unit.layout_type} />}
          <Box l="Net m²" v={`${unit.net_area} m²`} />
          <Box l="Brüt m²" v={`${unit.gross_area} m²`} />
          <Box l="Cephe" v={unit.aspect} />
          <Box l="Manzara" v={unit.view} />
          <Box l="Kat Konumu" v={unit.floor_position} />
          <Box l="Balkon" v={unit.has_balcony ? "Var" : "Yok"} />
          <Box l="Teras" v={unit.has_terrace ? "Var" : "Yok"} />
          <Box l="Bahçe" v={unit.has_garden ? "Var" : "Yok"} />
        </div>

        {project?.short_description && (
          <section className="mt-10">
            <h2 className="text-xl font-bold text-slate-900 font-display mb-2">Proje Hakkında</h2>
            <p className="text-slate-700">{project.short_description}</p>
          </section>
        )}

        {((unit.image_urls || []).length > 0 || unit.video_url) && (
          <section className="mt-10">
            <div className="mb-4">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Örnek Daire</div>
              <h2 className="text-xl font-bold text-slate-900 font-display">Görseller ve Video</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              {unitImages.slice(0, 6).map((url, i) => (
                <button key={url} type="button" onClick={() => setImageIndex(i)}
                        className="block aspect-[4/3] rounded-xl overflow-hidden bg-slate-100 text-left">
                  <img src={url} alt="" className="w-full h-full object-cover hover:scale-105 transition duration-300" />
                </button>
              ))}
            </div>
            {unit.video_url && (
              <div className="mt-4 bg-white border rounded-xl overflow-hidden" style={{ borderColor: "#E5E7EB" }}>
                <div className="px-4 py-3 text-sm font-semibold text-slate-900">Örnek Daire Videosu</div>
                <div className="aspect-video bg-slate-100">
                  {unitVideoEmbedUrl ? (
                    <iframe src={unitVideoEmbedUrl} title="Örnek daire videosu" className="w-full h-full" allowFullScreen />
                  ) : (
                    <video src={unit.video_url} controls className="w-full h-full object-cover" />
                  )}
                </div>
              </div>
            )}
          </section>
        )}

        {(unit.plan_url || unit.floor_plan_url || unit.seating_plan_url) && (
          <section className="mt-10">
            <div className="mb-4">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Planlar</div>
              <h2 className="text-xl font-bold text-slate-900 font-display">Daire ve Kat Planları</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {unit.plan_url && <PlanCard title="Daire Planı" url={unit.plan_url} onOpen={() => setPlanModal({ title: "Daire Planı", url: unit.plan_url })} />}
              {unit.floor_plan_url && <PlanCard title="Kat Planı" url={unit.floor_plan_url} onOpen={() => setPlanModal({ title: "Kat Planı", url: unit.floor_plan_url })} />}
              {unit.seating_plan_url && <PlanCard title="Kat / Konum Planı" url={unit.seating_plan_url} onOpen={() => setPlanModal({ title: "Kat / Konum Planı", url: unit.seating_plan_url })} />}
            </div>
          </section>
        )}

        <section className="mt-10 bg-white border rounded-xl p-6" style={{ borderColor: "#E5E7EB" }}>
          <div className="text-base font-semibold text-slate-900 mb-3">İletişim</div>
          <div className="grid md:grid-cols-3 gap-3 text-sm">
            {company?.phone && <div><div className="text-xs text-slate-500">Telefon</div><div className="font-medium">{company.phone}</div></div>}
            {company?.email && <div><div className="text-xs text-slate-500">E-posta</div><div className="font-medium">{company.email}</div></div>}
            {company?.website && <div><div className="text-xs text-slate-500">Web</div><a className="font-medium underline" href={company.website} target="_blank" rel="noreferrer">{company.website}</a></div>}
          </div>
        </section>

        {project?.public_inventory_enabled !== false && <div className="mt-8 text-center">
          <Link to={`/p/projects/${project ? unit.project_id : ""}`} className="text-sm underline text-slate-600">Tüm proje stoğuna geri dön</Link>
        </div>}
        <PublicLeadForm unitId={id} />
      </main>

      <footer className="border-t bg-white mt-10" style={{ borderColor: "#E5E7EB" }}>
        <div className="max-w-5xl mx-auto px-6 py-6 text-center text-sm text-slate-500">
          {company?.presentation_footer || `© ${company?.name}`}
          <div className="text-xs text-slate-400 mt-1">Powered by İnşaat Paneli</div>
        </div>
      </footer>

      {activeImageUrl && (
        <MediaModal title="Örnek Daire Görselleri" subtitle={`${imageIndex + 1} / ${unitImages.length}`}
                    onClose={() => setImageIndex(null)}>
          <div className="relative bg-slate-950">
            <img src={activeImageUrl} alt="" className="w-full max-h-[72vh] object-contain" />
            {unitImages.length > 1 && (
              <>
                <button type="button" onClick={showPrevImage}
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 text-slate-900 text-2xl leading-none hover:bg-white"
                        aria-label="Önceki görsel">
                  ‹
                </button>
                <button type="button" onClick={showNextImage}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 text-slate-900 text-2xl leading-none hover:bg-white"
                        aria-label="Sonraki görsel">
                  ›
                </button>
              </>
            )}
          </div>
        </MediaModal>
      )}

      {planModal && (
        <MediaModal title={planModal.title} subtitle="Plan önizleme" onClose={() => setPlanModal(null)}>
          <div className="bg-slate-950">
            <img src={planModal.url} alt={planModal.title} className="w-full max-h-[72vh] object-contain" />
          </div>
        </MediaModal>
      )}
    </div>
  );
}

function Box({ l, v }) {
  return (
    <div className="bg-white border rounded-xl p-3" style={{ borderColor: "#E5E7EB" }}>
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{l}</div>
      <div className="mt-1 font-semibold text-slate-900 text-sm">{v}</div>
    </div>
  );
}

function PlanCard({ title, url, onOpen }) {
  return (
    <button type="button" onClick={onOpen}
            className="block bg-white border rounded-xl overflow-hidden hover:shadow-md transition text-left"
            style={{ borderColor: "#E5E7EB" }}>
      <div className="aspect-[4/3] bg-slate-100">
        <img src={url} alt="" className="w-full h-full object-contain" />
      </div>
      <div className="p-3">
        <div className="text-sm font-semibold text-slate-900">{title}</div>
        <div className="text-xs text-slate-500">Büyütmek için tıklayın</div>
      </div>
    </button>
  );
}

function MediaModal({ title, subtitle, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-950/80 px-4 py-6 flex items-center justify-center"
         onClick={onClose}>
      <div className="relative w-full max-w-6xl" onClick={(e) => e.stopPropagation()}>
        <div className="bg-white rounded-xl overflow-hidden shadow-2xl">
          <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: "#E5E7EB" }}>
            <div>
              <div className="text-sm font-semibold text-slate-900">{title}</div>
              {subtitle && <div className="text-xs text-slate-500">{subtitle}</div>}
            </div>
            <button type="button" onClick={onClose}
                    className="px-3 py-1.5 text-sm border rounded-lg hover:bg-slate-50"
                    style={{ borderColor: "#E5E7EB" }}>
              Kapat
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

function getVideoEmbedUrl(url) {
  if (!url) return "";
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes("youtu.be")) {
      const id = parsed.pathname.replace("/", "");
      return id ? `https://www.youtube.com/embed/${id}` : "";
    }
    if (parsed.hostname.includes("youtube.com")) {
      if (parsed.pathname.startsWith("/embed/")) return url;
      const id = parsed.searchParams.get("v");
      return id ? `https://www.youtube.com/embed/${id}` : "";
    }
  } catch {
    return "";
  }
  return "";
}
